import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, Edit, Trash2, Eye, Calendar, FileText, TrendingUp, Clock, MoreHorizontal, Copy, Share2, X } from "lucide-react"
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Checkbox } from "@/components/ui/checkbox"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

const initialNews = [
  { id: 1, title: "Yeni Depomuz Açıldı", category: "Duyuru", status: "Yayında", date: "18.02.2026", views: 2450, author: "Admin", image: "/news/1.jpg", content: "Yeni depomuz İstanbul'da açıldı. Modern soğuk hava depolama sistemleri ile hizmetinizdeyiz." },
  { id: 2, title: "2026 Fiyat Listesi Güncellendi", category: "Fiyat", status: "Yayında", date: "15.02.2026", views: 1820, author: "Editor", image: "/news/2.jpg", content: "2026 yılı fiyat listemiz güncellendi. Yeni fiyatlarımızdan yararlanın." },
  { id: 3, title: "Yaz Dönemi Kampanyası Başlıyor", category: "Kampanya", status: "Taslak", date: "10.02.2026", views: 0, author: "Admin", image: "/news/3.jpg", content: "Yaz dönemi kampanyamız yakında başlıyor. %20 indirim fırsatını kaçırmayın." },
  { id: 4, title: "Yeni Lojistik Ortaklığımız", category: "Haber", status: "Yayında", date: "05.02.2026", views: 3890, author: "Editor", image: "/news/4.jpg", content: "Uluslararası lojistik devi ile ortaklık anlaşması imzaladık." },
  { id: 5, title: "Kış Bakımı Hizmetleri", category: "Hizmet", status: "Yayında", date: "01.02.2026", views: 1200, author: "Admin", image: "/news/5.jpg", content: "Kış bakımı hizmetlerimiz hakkında detaylı bilgi." },
  { id: 6, title: "Yılın Lojistik Firması Ödülü", category: "Ödül", status: "Taslak", date: "28.01.2026", views: 0, author: "Editor", image: "/news/6.jpg", content: "Yılın lojistik firması ödülüne layık görüldük." },
]

export default function Haberler() {
  const [news, setNews] = useState(initialNews)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selectedNews, setSelectedNews] = useState<number[]>([])
  const [viewItem, setViewItem] = useState<typeof news[0] | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<number | null>(null)

  const filteredNews = useMemo(() => {
    let result = news

    if (searchTerm) {
      result = result.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (categoryFilter !== 'all') {
      result = result.filter(item =>
        item.category.toLowerCase() === categoryFilter.toLowerCase()
      )
    }

    return result
  }, [news, searchTerm, categoryFilter])

  const toggleSelectAll = () => {
    if (selectedNews.length === filteredNews.length) {
      setSelectedNews([])
    } else {
      setSelectedNews(filteredNews.map(n => n.id))
    }
  }

  const toggleSelectNews = (id: number) => {
    setSelectedNews(current =>
      current.includes(id)
        ? current.filter(newsId => newsId !== id)
        : [...current, id]
    )
  }

  const handleDelete = (id: number) => {
    setItemToDelete(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (itemToDelete) {
      setNews(current => current.filter(n => n.id !== itemToDelete))
      toast.success('Haber başarıyla silindi')
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }

  const handleBulkDelete = () => {
    setNews(current => current.filter(n => !selectedNews.includes(n.id)))
    toast.success(`${selectedNews.length} haber başarıyla silindi`)
    setSelectedNews([])
  }

  const handleCopy = (item: typeof news[0]) => {
    const newItem = {
      ...item,
      id: Math.max(...news.map(n => n.id)) + 1,
      title: `${item.title} (Kopya)`,
      status: 'Taslak' as const,
      date: new Date().toLocaleDateString('tr-TR'),
      views: 0
    }
    setNews(current => [...current, newItem])
    toast.success('Haber kopyalandı')
  }

  const handleShare = (item: typeof news[0]) => {
    navigator.clipboard.writeText(`https://buzmavi.com/haber/${item.id}`)
    toast.success('Link kopyalandı')
  }

  const stats = {
    total: news.length,
    published: news.filter(n => n.status === 'Yayında').length,
    draft: news.filter(n => n.status === 'Taslak').length,
    totalViews: news.reduce((acc, n) => acc + n.views, 0)
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Haberler & Duyurular</h1>
            <p className="text-sm text-muted-foreground">Haber ve duyurularınızı yönetin</p>
          </div>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Yeni Haber
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Toplam Haber</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">+3 bu ay</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Yayında</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.published}</div>
              <p className="text-xs text-muted-foreground">{Math.round(stats.published / stats.total * 100)}% yayında</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Taslak</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.draft}</div>
              <p className="text-xs text-muted-foreground">Bekliyor</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Toplam Görüntülenme</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(stats.totalViews / 1000).toFixed(1)}K</div>
              <p className="text-xs text-muted-foreground">+12% bu ay</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <CardTitle>Tüm Haberler</CardTitle>
                {selectedNews.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{selectedNews.length} seçili</span>
                    <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                      <Trash2 className="mr-2 h-4 w-4" /> Sil
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]">
                    <SelectValue placeholder="Kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="Duyuru">Duyuru</SelectItem>
                    <SelectItem value="Haber">Haber</SelectItem>
                    <SelectItem value="Kampanya">Kampanya</SelectItem>
                    <SelectItem value="Fiyat">Fiyat</SelectItem>
                    <SelectItem value="Hizmet">Hizmet</SelectItem>
                    <SelectItem value="Ödül">Ödül</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Input placeholder="Haber ara..." className="w-full sm:w-[200px] pr-8" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox checked={selectedNews.length === filteredNews.length && filteredNews.length > 0} onCheckedChange={toggleSelectAll} />
                  </TableHead>
                  <TableHead>Haber</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Yazar</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Görüntülenme</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNews.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Checkbox checked={selectedNews.includes(item.id)} onCheckedChange={() => toggleSelectNews(item.id)} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={item.image} />
                          <AvatarFallback><FileText className="h-4 w-4" /></AvatarFallback>
                        </Avatar>
                        <span className="font-medium max-w-[200px] truncate">{item.title}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{item.category}</Badge></TableCell>
                    <TableCell>{item.author}</TableCell>
                    <TableCell>
                      <Badge variant={item.status === "Yayında" ? "default" : "secondary"}>{item.status}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {item.date}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{item.views.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewItem(item)}>
                            <Eye className="mr-2 h-4 w-4" /> Görüntüle
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="mr-2 h-4 w-4" /> Düzenle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare(item)}>
                            <Share2 className="mr-2 h-4 w-4" /> Paylaş
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopy(item)}>
                            <Copy className="mr-2 h-4 w-4" /> Kopyala
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Sil
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      </Main>

      {/* View Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {viewItem?.title}
            </DialogTitle>
            <DialogDescription>Haber detayları</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Kategori</label>
                <p><Badge variant="outline">{viewItem?.category}</Badge></p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Durum</label>
                <p><Badge variant={viewItem?.status === "Yayında" ? "default" : "secondary"}>{viewItem?.status}</Badge></p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Yazar</label>
                <p>{viewItem?.author}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Görüntülenme</label>
                <p>{viewItem?.views.toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tarih</label>
                <p>{viewItem?.date}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">İçerik</label>
              <p className="mt-1 p-3 bg-muted rounded-md">{viewItem?.content}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewItem(null)}>Kapat</Button>
            <Button onClick={() => handleShare(viewItem!)}><Share2 className="mr-2 h-4 w-4" /> Paylaş</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Haberi Sil</DialogTitle>
            <DialogDescription>Bu haberi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Sil</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
